import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { campaignAPI, projectAPI } from '../../services/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { ArrowLeft, BarChart3, Copy, ExternalLink, Image, Link2, Loader2, Plus, Settings2, TrendingUp, Users, MousePointer, DollarSign, Globe, ListChecks } from 'lucide-react';
import EscrowFundPanel from '@/components/client/EscrowFundPanel';
import CampaignTypeBadge from '@/components/campaign/CampaignTypeBadge';
import CampaignRouteExplainer from '@/components/campaign/CampaignRouteExplainer';
import RegistrationFormBuilder from '@/components/campaign/RegistrationFormBuilder';
import TypedContractBuilder from '@/components/campaign/TypedContractBuilder';
import { resolveCampaignGoal } from '@/lib/campaignGoals/compatibility';
import {
  TailAdminDetailRow,
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminProgressRow,
  TailAdminStatusBadge,
} from '@/components/template/tailadmin/DashboardPrimitives';

const statusColors = {
  draft: 'neutral',
  active: 'success',
  paused: 'warning',
  ended: 'danger'
};

const ClientCampaignDetail = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [stats, setStats] = useState(null);
  const [assets, setAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(true);
  const [assetsUnavailable, setAssetsUnavailable] = useState(false);
  const [savingAsset, setSavingAsset] = useState(false);
  const [assetForm, setAssetForm] = useState({ type: 'image', title: '', url: '' });
  const [loading, setLoading] = useState(true);
  const [setup, setSetup] = useState(null);
  const [savingForm, setSavingForm] = useState(false);
  const [offerForm, setOfferForm] = useState(null);
  const [savingOffer, setSavingOffer] = useState(false);
  const [contractState, setContractState] = useState({ contracts: [], required_purposes: [], missing_purposes: [], ready: false, active_environment: null });
  const [projectContracts, setProjectContracts] = useState([]);
  const [savingContract, setSavingContract] = useState(false);
  const [activeView, setActiveView] = useState('overview');

  const loadAssets = useCallback(async () => {
    setAssetsLoading(true);
    setAssetsUnavailable(false);
    try {
      const res = await campaignAPI.getAssets(campaignId);
      const list = res?.assets || res?.data?.assets || (Array.isArray(res) ? res : []);
      setAssets(list);
    } catch (error) {
      if (error.response?.status === 404 || error.response?.status === 501) {
        setAssetsUnavailable(true);
      }
      setAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  }, [campaignId]);

  const loadData = useCallback(async () => {
    try {
      const [campaignRes, statsRes] = await Promise.all([
        campaignAPI.getCampaign(campaignId),
        campaignAPI.getCampaignStats(campaignId)
      ]);
      const loadedCampaign = campaignRes.campaign;
      setCampaign(loadedCampaign);
      setStats(statsRes.stats);
      const projectId = loadedCampaign?.project_id || loadedCampaign?.projectId;
      if (projectId) {
        try {
          const response = await projectAPI.listContracts(projectId);
          setProjectContracts(response.data?.contracts || []);
        } catch {
          // Project-library availability should not make the campaign itself
          // appear unavailable; founders can still use campaign-local setup.
          setProjectContracts([]);
          toast.warning('Reusable Project contracts are temporarily unavailable');
        }
      }
    } catch (error) {
      console.error('Error loading campaign:', error);
      toast.error('Failed to load campaign details');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const handleAddAsset = async (e) => {
    e.preventDefault();
    if (!assetForm.title?.trim() || !assetForm.url?.trim()) {
      toast.error('Title and URL are required');
      return;
    }
    setSavingAsset(true);
    try {
      await campaignAPI.createAsset(campaignId, {
        type: assetForm.type,
        title: assetForm.title.trim(),
        url: assetForm.url.trim(),
        source: 'client_upload',
      });
      toast.success('Asset added');
      setAssetForm({ type: 'image', title: '', url: '' });
      loadAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add asset — API may not be live yet');
    } finally {
      setSavingAsset(false);
    }
  };

  useEffect(() => {
    loadData();
    loadAssets();
    campaignAPI.getSetup(campaignId)
      .then((response) => setSetup(response.setup))
      .catch(() => setSetup(null));
    campaignAPI.listContracts(campaignId)
      .then((response) => setContractState(response))
      .catch(() => setContractState({ contracts: [], required_purposes: [], missing_purposes: [], ready: false, active_environment: null }));
  }, [campaignId, loadData, loadAssets]);

  const handleSaveRegistrationForm = async (fields) => {
    setSavingForm(true);
    try {
      const response = await campaignAPI.saveRegistrationForm(campaignId, fields);
      setSetup((current) => ({ ...current, registration_form: response.registration_form }));
      toast.success('Registration form saved as a new version');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save registration form');
    } finally {
      setSavingForm(false);
    }
  };

  const handleFounderOffer = async () => {
    if (!offerForm?.brmId) return;
    setSavingOffer(true);
    try {
      await campaignAPI.proposeNegotiation(campaignId, {
        brmId: offerForm.brmId,
        commissionPerGoal: Number(offerForm.commissionPerGoal),
        mediaFee: Number(offerForm.mediaFee),
        audiencePayModel: setup?.audience_pay_model || 'performance_triggered_media_fee',
        qualificationRule: { minimum_conversions: 1 },
      });
      toast.success('Private Negotiation Card sent');
      setOfferForm(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send Negotiation Card');
    } finally {
      setSavingOffer(false);
    }
  };

  const reloadContracts = async () => {
    const response = await campaignAPI.listContracts(campaignId);
    setContractState(response);
    setSetup((current) => current ? ({ ...current, integration_validated: response.ready }) : current);
  };

  const reloadProjectContracts = async () => {
    const projectId = campaign?.project_id || campaign?.projectId;
    if (!projectId) return;
    const response = await projectAPI.listContracts(projectId);
    setProjectContracts(response.data?.contracts || []);
  };

  const handleSaveContract = async (data) => {
    setSavingContract(true);
    try {
      await campaignAPI.saveContract(campaignId, data);
      await Promise.all([reloadContracts(), reloadProjectContracts()]);
      toast.success('Integration contract saved; run its test before publishing');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save integration contract');
    } finally {
      setSavingContract(false);
    }
  };

  const handleTestContract = async (contractId) => {
    setSavingContract(true);
    try {
      const response = await campaignAPI.testContract(campaignId, contractId);
      await Promise.all([reloadContracts(), reloadProjectContracts()]);
      toast[response.test?.success ? 'success' : 'error'](response.test?.message || 'Contract test finished');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Contract test failed');
    } finally {
      setSavingContract(false);
    }
  };

  const handleBindContract = async (contractId) => {
    const projectId = campaign?.project_id || campaign?.projectId;
    if (!projectId) return;
    setSavingContract(true);
    try {
      await projectAPI.bindContract(projectId, contractId, campaignId);
      await reloadContracts();
      toast.success('Verified Project contract bound to this Proove Request');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reuse Project contract');
    } finally {
      setSavingContract(false);
    }
  };

  const handlePublish = async () => {
    try {
      await campaignAPI.publishCampaign(campaignId);
      toast.success('Campaign published successfully!');
      loadData();
    } catch (error) {
      console.error('Error publishing campaign:', error);
      toast.error(error.response?.data?.detail || 'Failed to publish campaign');
    }
  };

  const copyLink = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-500"></div>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-6 sm:px-6">
        <TailAdminEmptyState
          icon={Globe}
          title="Campaign not found"
          description="This campaign could not be loaded or is no longer available."
          action={<Button variant="outline" onClick={() => navigate('/client/campaigns')}>Back to Campaigns</Button>}
        />
      </div>
    );
  }

  const campaignProductId = campaign.product_id || campaign.productId;
  const campaignGoal = resolveCampaignGoal(campaign);
  const campaignsListPath = campaignProductId
    ? `/client/products/${campaignProductId}/campaigns`
    : '/client/campaigns';

  // Calculate metrics
  const totalClicks = stats?.total_clicks || 0;
  const totalConversions = stats?.total_conversions || 0;
  const totalRevenue = stats?.total_revenue || 0;
  const conversionRate = totalClicks > 0 ? ((totalConversions / totalClicks) * 100).toFixed(2) : 0;
  const avgRevenuePerClick = totalClicks > 0 ? (totalRevenue / totalClicks).toFixed(2) : 0;
  const budgetSpent = Number(campaign.budget_spent || 0);
  const budgetCap = Number(campaign.budget_cap || 0);
  const roi = budgetSpent > 0 ? (((totalRevenue - budgetSpent) / budgetSpent) * 100).toFixed(2) : 0;
  const isDirectV2 = setup?.request_type === 'direct' && setup?.direct_version === 'v2';
  const isDirectV1 = setup?.request_type === 'direct' && setup?.direct_version === 'v1';
  const agentCampaignPath = `/brm/campaigns/${campaignId}`;
  const agentCampaignUrl = typeof window !== 'undefined' ? `${window.location.origin}${agentCampaignPath}` : agentCampaignPath;

  const geoData = Array.isArray(stats?.geo_performance) ? stats.geo_performance : [];

  return (
    <div className="mx-auto max-w-7xl space-y-5 px-4 py-6 sm:px-6">
      <TailAdminPageHeader
        eyebrow="Campaign detail"
        title={campaign.title}
        description={campaign.description}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(campaignsListPath)}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            {campaignProductId ? (
              <Button
                variant="outline"
                onClick={() => navigate(`/client/products/${campaignProductId}/integration`)}
              >
                <Link2 className="mr-2 h-4 w-4" />
                Integration
              </Button>
            ) : null}
            <TailAdminStatusBadge tone={statusColors[campaign.status] || 'neutral'}>
              {campaign.status}
            </TailAdminStatusBadge>
            <CampaignTypeBadge
              requestType={campaign.request_type || setup?.request_type}
              directVersion={campaign.direct_version || setup?.direct_version}
            />
            <CampaignRouteExplainer />
            {campaign.status === 'draft' && campaignGoal.publishable && (
              <Button onClick={handlePublish} title={contractState.ready ? 'Funding is also checked before publication' : 'The server will verify contracts and funding before publication'}>
                Publish to marketplace
              </Button>
            )}
            {campaign.status === 'draft' && !campaignGoal.publishable && (
              <span className="text-sm font-medium text-amber-700">Read-only goal · publishing unavailable</span>
            )}
          </>
        }
      />

      {campaign.status === 'draft' && campaignGoal.publishable && (
        <section className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs sm:flex-row sm:items-center sm:justify-between" aria-label="Campaign readiness">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-[#1070f1]">Next action</p>
            <h2 className="mt-1 text-xl font-semibold">{contractState.ready ? 'Fund escrow, then publish' : 'Verify your subscription connection'}</h2>
            <p className="mt-1 text-sm text-gray-600">{contractState.ready ? 'Your connection is verified. The server will confirm funding before publication.' : `${contractState.missing_purposes?.length || 1} required contract${contractState.missing_purposes?.length === 1 ? '' : 's'} still need verification.`}</p>
          </div>
          <TailAdminStatusBadge tone={contractState.ready ? 'success' : 'warning'}>{contractState.ready ? 'Connection ready' : 'Draft · action needed'}</TailAdminStatusBadge>
        </section>
      )}

      {isDirectV2 && (
        <nav className="grid grid-cols-3 gap-1 rounded-xl border border-gray-200 bg-white p-1 shadow-theme-xs" aria-label="Campaign detail sections">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'questions', label: 'Customer form', icon: ListChecks },
            { id: 'developer', label: 'Developer setup', mobileLabel: 'Developer', icon: Settings2 },
          ].map(({ id, label, mobileLabel, icon: Icon }) => (
            <button
              key={id}
              type="button"
              onClick={() => setActiveView(id)}
              className={`inline-flex min-h-11 min-w-0 items-center justify-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 sm:gap-2 sm:px-4 sm:text-sm ${activeView === id ? 'bg-brand-50 text-brand-500' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}
              aria-current={activeView === id ? 'page' : undefined}
              aria-label={label}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className={mobileLabel ? 'hidden sm:inline' : ''}>{label}</span>
              {mobileLabel ? <span className="sm:hidden">{mobileLabel}</span> : null}
            </button>
          ))}
        </nav>
      )}

      {isDirectV1 && (
        <TailAdminPanel
          title="Campaign links"
          description="Use the customer link for signup. The Agent page is a separate signed-in workspace."
          action={<Link2 className="h-5 w-5 text-brand-500" />}
        >
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">Customer signup link</p>
              <p className="mt-1 break-all text-sm text-gray-500">{campaign.target_url || 'No customer link configured'}</p>
              {campaign.target_url ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" asChild><a href={campaign.target_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Open signup page</a></Button>
                  <Button size="sm" variant="ghost" aria-label="Copy customer signup link" onClick={() => copyLink(campaign.target_url, 'Customer signup link')}><Copy className="mr-2 h-4 w-4" />Copy link</Button>
                </div>
              ) : null}
            </div>
            <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
              <p className="text-sm font-semibold text-gray-900">Agent campaign page</p>
              <p className="mt-1 break-all text-sm text-gray-500">{agentCampaignUrl}</p>
              <p className="mt-1 text-xs text-gray-500">Opens the Agent view and requires Agent sign-in.</p>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button size="sm" variant="outline" asChild><a href={agentCampaignPath} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Open Agent view</a></Button>
                <Button size="sm" variant="ghost" aria-label="Copy Agent campaign link" onClick={() => copyLink(agentCampaignUrl, 'Agent campaign link')}><Copy className="mr-2 h-4 w-4" />Copy link</Button>
              </div>
            </div>
          </div>
          <p className="mt-3 text-xs leading-5 text-gray-500">An introduction or signup does not earn a reward by itself. The Agent reward becomes eligible only after the subscription is verified.</p>
        </TailAdminPanel>
      )}

      {isDirectV2 && activeView === 'overview' && (
        <>
          <TailAdminPanel
            title="Campaign access"
            description="Open or share the live destinations used by customers and Agents."
            action={<Link2 className="h-5 w-5 text-brand-500" />}
          >
              <div className="grid gap-3 lg:grid-cols-2">
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-900">Customer destination</p>
                  <p className="mt-1 break-all text-sm text-gray-500">{campaign.target_url || 'No target URL configured'}</p>
                  {campaign.target_url ? (
                    <div className="mt-3 flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" asChild><a href={campaign.target_url} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Open</a></Button>
                      <Button size="sm" variant="ghost" onClick={() => copyLink(campaign.target_url, 'Customer link')}><Copy className="mr-2 h-4 w-4" />Copy link</Button>
                    </div>
                  ) : null}
                </div>
                <div className="rounded-xl border border-gray-100 bg-gray-50 p-4">
                  <p className="text-sm font-semibold text-gray-900">Agent campaign page</p>
                  <p className="mt-1 break-all text-sm text-gray-500">{agentCampaignUrl}</p>
                  <p className="mt-1 text-xs text-gray-500">Opens the Agent view and requires Agent sign-in.</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" asChild><a href={agentCampaignPath} target="_blank" rel="noopener noreferrer"><ExternalLink className="mr-2 h-4 w-4" />Open Agent view</a></Button>
                    <Button size="sm" variant="ghost" onClick={() => copyLink(agentCampaignUrl, 'Agent campaign link')}><Copy className="mr-2 h-4 w-4" />Copy link</Button>
                  </div>
                </div>
              </div>
          </TailAdminPanel>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <TailAdminMetricCard label="Total Clicks" value={totalClicks.toLocaleString()} icon={MousePointer} helper={`From ${campaign.participation_count || 0} BRMs`} />
            <TailAdminMetricCard label="Conversions" value={totalConversions.toLocaleString()} icon={TrendingUp} tone="success" helper={`${conversionRate}% conversion rate`} />
            <TailAdminMetricCard label="Revenue Generated" value={`₦${totalRevenue.toLocaleString()}`} icon={DollarSign} tone="success" helper={`₦${avgRevenuePerClick} per click`} />
            <TailAdminMetricCard label="ROI" value={`${roi}%`} icon={TrendingUp} tone={parseFloat(roi) >= 0 ? 'success' : 'danger'} helper={`₦${budgetSpent.toLocaleString()} spent`} />
          </div>
        </>
      )}

      {(!isDirectV2 || activeView === 'overview') && (campaignGoal.publishable ? (
        <EscrowFundPanel campaignId={campaignId} />
      ) : (
        <TailAdminPanel
          title="Escrow unavailable"
          description="Funding is disabled because this goal is read-only in the Subscription beta."
        />
      ))}

      {setup && (!isDirectV2 || activeView === 'questions') && (
        <TailAdminPanel
          title="Founder setup"
          description="Request type, conversion contract, and publication readiness"
          action={
            <CampaignTypeBadge
              requestType={setup.request_type}
              directVersion={setup.direct_version}
            />
          }
        >
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <TailAdminDetailRow label="Qualification" value={setup.qualification_event || campaign.goal_type} />
            <TailAdminDetailRow label="Commission policy" value={(setup.commission_policy || '').replaceAll('_', ' ')} />
            <TailAdminDetailRow label="Attribution" value={(setup.attribution_policy || '').replaceAll('_', ' ')} />
            <TailAdminDetailRow
              label="Connection"
              value={
                <TailAdminStatusBadge tone={setup.integration_validated ? 'success' : 'warning'}>
                  {setup.integration_validated ? 'Verified' : 'Verification required'}
                </TailAdminStatusBadge>
              }
            />
          </div>
          {setup.request_type === 'broadcast' && (
            <div className="mt-4 rounded-xl border border-gray-200 bg-gray-50 p-3 text-sm text-gray-700">
              Audience pay is <strong>{setup.audience_pay_enabled ? 'enabled' : 'disabled'}</strong>
              {setup.audience_pay_model ? ` · ${setup.audience_pay_model.replaceAll('_', ' ')}` : ''}.
              Accepted Agent terms are stored as immutable Negotiation Card versions.
            </div>
          )}
        </TailAdminPanel>
      )}

      {isDirectV2 && activeView === 'questions' && (
        <TailAdminPanel
          title="Your customer questions"
          description={`Direct V2 form · version ${setup.registration_form?.version || 0}. Agents only see the approved version on the campaign page.`}
          action={null}
        >
          <RegistrationFormBuilder
            initialFields={setup.registration_form?.fields}
            onSave={handleSaveRegistrationForm}
            saving={savingForm}
          />
        </TailAdminPanel>
      )}

      {setup?.request_type && (!isDirectV2 || activeView === 'developer') && (
        <details className="rounded-2xl border border-gray-200 bg-white p-5 shadow-theme-xs">
          <summary className="cursor-pointer rounded-lg text-base font-semibold text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2">For your developer: integration contracts</summary>
          <p className="mt-1 text-sm text-gray-500">Developer settings · Secrets are encrypted and every required contract must pass before publication.</p>
          <div className="mt-5">
          <TypedContractBuilder
            contracts={contractState.contracts || []}
            reusableContracts={projectContracts}
            requiredPurposes={contractState.required_purposes || []}
            activeEnvironment={contractState.active_environment}
            onSave={handleSaveContract}
            onTest={handleTestContract}
            onBind={handleBindContract}
            saving={savingContract}
          />
          </div>
        </details>
      )}

      {!isDirectV2 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
          <TailAdminMetricCard label="Total Clicks" value={totalClicks.toLocaleString()} icon={MousePointer} helper={`From ${campaign.participation_count || 0} BRMs`} />
          <TailAdminMetricCard label="Conversions" value={totalConversions.toLocaleString()} icon={TrendingUp} tone="success" helper={`${conversionRate}% conversion rate`} />
          <TailAdminMetricCard label="Revenue Generated" value={`₦${totalRevenue.toLocaleString()}`} icon={DollarSign} tone="success" helper={`₦${avgRevenuePerClick} per click`} />
          <TailAdminMetricCard label="ROI" value={`${roi}%`} icon={TrendingUp} tone={parseFloat(roi) >= 0 ? 'success' : 'danger'} helper={`₦${budgetSpent.toLocaleString()} spent`} />
        </div>
      )}

      {/* Two Column Layout */}
      {(!isDirectV2 || activeView === 'overview') && <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Geographic Analysis */}
          <TailAdminPanel title="Geographic Performance" description="Performance breakdown by region" action={<Globe className="h-5 w-5 text-brand-500" />}>
              {geoData.length > 0 ? (
                <div className="space-y-4">
                  {geoData.map((geo, index) => {
                    const clickPercent = totalClicks > 0 ? ((geo.clicks / totalClicks) * 100).toFixed(1) : 0;
                    const conversionPercent = totalConversions > 0 ? ((geo.conversions / totalConversions) * 100).toFixed(1) : 0;
                    const regionConversionRate = geo.clicks > 0 ? ((geo.conversions / geo.clicks) * 100).toFixed(1) : 0;

                    return (
                      <div key={index} className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-sm font-bold text-brand-500">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium">{geo.region}</p>
                              <p className="text-xs text-gray-500">
                                {regionConversionRate}% conversion rate
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold">{geo.clicks} clicks</p>
                            <p className="text-xs text-emerald-600">{geo.conversions} conversions</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-gray-600">Click Share</span>
                              <span className="font-medium">{clickPercent}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full bg-brand-500 transition-all"
                                style={{ width: `${clickPercent}%` }}
                              />
                            </div>
                          </div>
                          <div>
                            <div className="flex items-center justify-between text-xs mb-1">
                              <span className="text-gray-600">Conversion Share</span>
                              <span className="font-medium">{conversionPercent}%</span>
                            </div>
                            <div className="w-full bg-gray-200 rounded-full h-2">
                              <div
                                className="h-2 rounded-full bg-emerald-500 transition-all"
                                style={{ width: `${conversionPercent}%` }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No geographic data available yet</p>
              )}
          </TailAdminPanel>

          {/* BRM Performance */}
          <TailAdminPanel title="BRM Performance" description="Top performing brand representatives" action={<Users className="h-5 w-5 text-brand-500" />}>
              {stats?.brm_performance && stats.brm_performance.length > 0 ? (
                <div className="space-y-3">
                  {stats.brm_performance.map((brm, index) => (
                    <div key={index} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
                      <div className="flex items-center space-x-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-500 font-bold text-white">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{brm.brm_name || 'BRM'}</p>
                          <p className="text-xs text-gray-500">{brm.brm_id}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{brm.clicks || 0} clicks</p>
                        <p className="text-xs text-emerald-600">{brm.conversions || 0} conversions</p>
                        <p className="text-xs text-gray-600">₦{(brm.revenue || 0).toLocaleString()} earned</p>
                        {setup?.request_type === 'broadcast' && setup?.audience_pay_enabled && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="mt-2"
                            onClick={() => setOfferForm({
                              brmId: brm.brm_id,
                              commissionPerGoal: Number(campaign.commission_per_goal || 0),
                              mediaFee: 0,
                            })}
                          >
                            Negotiate terms
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-8">No BRM performance data yet</p>
              )}
          </TailAdminPanel>

          {offerForm && (
            <TailAdminPanel title="Private Negotiation Card" description={`Offer for ${offerForm.brmId}`} action={null}>
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label htmlFor="offer-commission">Commission per conversion</Label>
                  <Input
                    id="offer-commission"
                    type="number"
                    min="0"
                    value={offerForm.commissionPerGoal}
                    onChange={(event) => setOfferForm((current) => ({ ...current, commissionPerGoal: event.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="offer-media-fee">Media fee</Label>
                  <Input
                    id="offer-media-fee"
                    type="number"
                    min="0"
                    value={offerForm.mediaFee}
                    onChange={(event) => setOfferForm((current) => ({ ...current, mediaFee: event.target.value }))}
                  />
                </div>
              </div>
              <div className="mt-3 flex gap-2">
                <Button onClick={handleFounderOffer} disabled={savingOffer}>{savingOffer ? 'Sending…' : 'Send private offer'}</Button>
                <Button variant="outline" onClick={() => setOfferForm(null)}>Cancel</Button>
              </div>
            </TailAdminPanel>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Campaign Details */}
          <TailAdminPanel title="Campaign Details" action={null}>
            <div className="space-y-3">
              <TailAdminDetailRow label="Verified result" value={<TailAdminStatusBadge tone="primary">{campaignGoal.label}</TailAdminStatusBadge>} />
              <TailAdminDetailRow label="Agent reward" value={`₦${Number(campaign.commission_per_goal || 0).toLocaleString()}`} />
              <div className="rounded-xl border border-gray-100 p-3">
                <p className="text-sm font-medium text-gray-700">Customer signup link</p>
                <a href={campaign.target_url} target="_blank" rel="noopener noreferrer" className="mt-1 block break-all text-xs font-medium text-brand-500 hover:underline">
                  {campaign.target_url}
                </a>
              </div>
              {budgetCap > 0 && (
                <TailAdminProgressRow
                  label="Budget"
                  helper={`₦${budgetSpent.toLocaleString()} / ₦${budgetCap.toLocaleString()}`}
                  value={Math.round((budgetSpent / budgetCap) * 100)}
                />
              )}
              {campaign.min_followers > 0 && (
                <TailAdminDetailRow label="Min. Followers" value={campaign.min_followers.toLocaleString()} />
              )}
            </div>
          </TailAdminPanel>

          {/* Niches */}
          {campaign.niches && campaign.niches.length > 0 && (
            <TailAdminPanel title="Target Niches" action={null}>
                <div className="flex flex-wrap gap-2">
                  {campaign.niches.map(niche => (
                    <TailAdminStatusBadge key={niche} tone="neutral">{niche}</TailAdminStatusBadge>
                  ))}
                </div>
            </TailAdminPanel>
          )}

          {/* Schedule */}
          {(campaign.starts_at || campaign.ends_at) && (
            <TailAdminPanel title="Schedule" action={null}>
              <div className="space-y-3">
                {campaign.starts_at && (
                  <TailAdminDetailRow label="Starts" value={new Date(campaign.starts_at).toLocaleString()} />
                )}
                {campaign.ends_at && (
                  <TailAdminDetailRow label="Ends" value={new Date(campaign.ends_at).toLocaleString()} />
                )}
              </div>
            </TailAdminPanel>
          )}

          {/* Participants */}
          <TailAdminPanel title="Participation" action={null}>
              <div className="text-center">
                <p className="text-4xl font-bold text-brand-500">{campaign.participation_count || 0}</p>
                <p className="text-sm text-gray-600 mt-1">BRMs joined</p>
              </div>
          </TailAdminPanel>

          {/* Creative assets */}
          <TailAdminPanel
            title="Creative assets"
            description="Materials BRMs see in their campaign hub"
            action={<Image className="h-5 w-5 text-brand-500" />}
          >
            {assetsUnavailable && (
              <p className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                Asset API not available yet (expected GET/POST /campaigns/{'{id}'}/assets).
              </p>
            )}
            <form onSubmit={handleAddAsset} className="mb-4 space-y-3 rounded-xl border border-gray-100 bg-gray-50 p-3">
              <div className="space-y-1.5">
                <Label htmlFor="asset-type">Type</Label>
                <Select
                  value={assetForm.type}
                  onValueChange={(value) => setAssetForm((f) => ({ ...f, type: value }))}
                >
                  <SelectTrigger id="asset-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="image">Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="document">Document</SelectItem>
                    <SelectItem value="copy">Copy / text</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="asset-title">Title</Label>
                <Input
                  id="asset-title"
                  placeholder="e.g. Instagram story template"
                  value={assetForm.title}
                  onChange={(e) => setAssetForm((f) => ({ ...f, title: e.target.value }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="asset-url">URL</Label>
                <Input
                  id="asset-url"
                  type="url"
                  placeholder="https://..."
                  value={assetForm.url}
                  onChange={(e) => setAssetForm((f) => ({ ...f, url: e.target.value }))}
                />
              </div>
              <Button type="submit" size="sm" disabled={savingAsset || assetsUnavailable}>
                {savingAsset ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                Add asset
              </Button>
            </form>
            {assetsLoading ? (
              <p className="text-center text-sm text-gray-500 py-4">Loading assets…</p>
            ) : assets.length > 0 ? (
              <div className="space-y-2">
                {assets.map((asset) => (
                  <div
                    key={asset.asset_id || asset.assetId || asset.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-gray-100 p-2 text-sm"
                  >
                    <div className="min-w-0">
                      <p className="font-medium truncate">{asset.title || asset.type}</p>
                      <p className="text-xs text-gray-500 capitalize">{asset.type}</p>
                    </div>
                    {asset.url && (
                      <a
                        href={asset.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="shrink-0 text-xs font-medium text-brand-500 hover:underline"
                      >
                        Open
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-gray-500 py-4">No assets yet</p>
            )}
          </TailAdminPanel>
        </div>
      </div>}
    </div>
  );
};

export default ClientCampaignDetail;
